import { useState } from "react";

import {
  makeGetApiCall,
  makePostApiCall,
  makePutApiCall,
} from "../../shared/makeApiCall";
import { BannerNotificationType } from "../../components/SimpleNotification";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import ProgressModal from "../../components/modals/ProgressModal";

import ManufacturedItemInfo from "../../components/forms/manufacturedProducts/ManufacturedItemInfo";
import RemoveWipItemByIdForm from "../../components/forms/wips/RemoveWipItemByIdForm";
import DeleteWipOutputItemByIdForm from "../../components/forms/wips/DeleteWipOutputItemByIdForm";
import AddPriceGroupModal from "../../components/forms/wips/AddPriceGroupModal";
import { set } from "date-fns";
import { row } from "mathjs";

export function WipDetailsViewModel(logout, wipId) {
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [showSimpleNotification, setShowSimpleNotification] = useState({
    show: false,
    message: null,
    title: null,
    type: BannerNotificationType.Success,
  });
  const [roomOptions, setRoomOptions] = useState([]);
  const [removeReasonFromWipOptions, setRemoveReasonFromWipOptions] = useState(
    [],
  );
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [wip, setWip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [outgoingSelectedItems, setOutgoingSelectedItems] = useState([]);
  const [rowDetails, setRowDetails] = useState({}); // { [rowId]: detailData }
  const [loadingRows, setLoadingRows] = useState({}); // { [rowId]: boolean }
  const [subRowDetails, setSubRowDetails] = useState({}); // { [rowId]: detailData }
  const [subLoadingRows, setSubLoadingRows] = useState({}); // { [rowId]: boolean }
  const [lastRow, setLastRow] = useState(null);
  const [outgoingRowDetails, setOutgoingRowDetails] = useState({}); // { [rowId]: detailData }
  const [outgoingLoadingRows, setOutgoingLoadingRows] = useState({}); // { [rowId]: boolean }
  const [outgoingSubRowDetails, setOutgoingSubRowDetails] = useState({}); // { [rowId]: detailData }
  const [outgoingSubLoadingRows, setOutgoingSubLoadingRows] = useState({}); // { [rowId]: boolean }
  const [outgoingLastRow, setOutgoingLastRow] = useState(null);
  const [outgoingParentRow, setOutgoingParentRow] = useState(null);

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title ? title : "Error"}
        />
      ),
      open: true,
    });
  };

  const getWipDetails = async function () {
    setLoading(true);
    let callResult = await makeGetApiCall(`wips/${wipId}`);
    if (callResult.success) {
      setWip(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch Wip information");
    }
    setLoading(false);
    return callResult;
  };

  const getRoomOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`roomsForDropdown`);

    if (callResult.success) {
      setRoomOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch rooms");
    }
  };

  const updateWip = async function (payload) {
    let callResult = await makePutApiCall(`wips/${wip.id}`, {
      ...payload,
    });

    if (callResult.success) {
      setShowSimpleNotification({
        show: true,
        title: "WIP updated",
        type: BannerNotificationType.Success,
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const onAddItemToWipByBarcode = async function (scannedBarcode) {
    let callResult = await makePostApiCall("wipItems/add", {
      wip_id: wip.id,
      barcode: scannedBarcode,
    });

    if (callResult.success) {
      setWip({
        ...wip,
        incoming_items: callResult.data.items,
        incoming_info: callResult.data.info,
      });
      setShowSimpleNotification({
        show: true,
        title: "Item added to WIP",
        type: BannerNotificationType.Success,
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const onRemoveItemWipByBarcode = async function (payload) {
    let callResult = await makePostApiCall("wipItems/remove", {
      wip_id: wip.id,
      ...payload,
    });

    if (callResult.success) {
      setWip({
        ...wip,
        incoming_items: callResult.data.incoming_items.items,
        incoming_info: callResult.data.incoming_items.info,
        removed_items: callResult.data.removed_items,
      });
      setShowSimpleNotification({
        show: true,
        title: "Item removed from WIP",
        type: BannerNotificationType.Success,
      });

      setRowDetails({});
      setSubRowDetails({});

      setEmptyModelOpen({ open: false, childComponent: null });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const getRemoveReasonsOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`reasonCodesForDropdown`);

    if (callResult.success) {
      setRemoveReasonFromWipOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch reasons to remove");
    }
  };

  const onGetWipItemByBarcode = async function (barcode) {
    let callResult = await makeGetApiCall(`wipItems/barcode/${barcode}`);

    if (callResult.success) {
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch barcode information");
    }
    return callResult;
  };

  const openSlideForManufacturedItemDetails = async function (
    manProdId,
    manProdPalletId,
  ) {
    var callResult = { success: false };
    if (manProdPalletId) {
      callResult = await makeGetApiCall(
        `manufacturedPallets/${manProdPalletId}`,
      );
    } else {
      callResult = await makeGetApiCall(`manufacturedProducts/${manProdId}`);
    }
    if (callResult.success) {
      setSlideOver({
        childComponent: (
          <ManufacturedItemInfo item={callResult.data} setOpen={setSlideOver} />
        ),
        open: true,
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch manufactured item");
    }
  };

  const onAskReturnIncomingItem = (wipItem) => {
    setEmptyModelOpen({
      open: true,
      childComponent: (
        <RemoveWipItemByIdForm
          wip={wip}
          onRemoveItemWipByBarcode={onRemoveItemWipByBarcode}
          removeReasonFromWipOptions={removeReasonFromWipOptions}
          setEmptyModelOpen={setEmptyModelOpen}
          item={wipItem}
        />
      ),
    });
  };

  const onDeleteWipOutputItemByBarcode = async function (
    deletedProduct,
    reason,
  ) {
    let callResult = await makePostApiCall(
      `manufactureProducts/deleteByBarcode/${deletedProduct.barcode}`,
      {
        reason_id: reason.value,
        reason_name: reason.label,
        wip_id: wipId,
      },
    );

    if (callResult.success) {
      setWip({
        ...wip,
        outgoing_items: callResult.data.outgoing_items.items,
      });
      setShowSimpleNotification({
        show: true,
        title: "Carton deleted from outgoing WIP",
        type: BannerNotificationType.Success,
      });

      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });

      if (outgoingParentRow) {
        fetchOutgoingRowDetails(outgoingParentRow, false);
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const onAskDeleteWipOutputItem = (wipItem) => {
    setEmptyModelOpen({
      open: true,
      childComponent: (
        <DeleteWipOutputItemByIdForm
          onDeleteWipOutputItemByBarcode={(reason) =>
            onDeleteWipOutputItemByBarcode(wipItem, reason)
          }
          removeReasonFromWipOptions={removeReasonFromWipOptions}
          setEmptyModelOpen={setEmptyModelOpen}
          item={wipItem}
        />
      ),
    });
  };

  const onCreatePriceGroup = async function (costPercentage) {
    if (outgoingSelectedItems.length < 1) {
      showErrorModal("Please select outgoing items");
    } else {
      // Close the input modal and show progress modal
      setEmptyModelOpen({
        open: true,
        childComponent: <ProgressModal message="Creating price group..." />,
      });

      let callResult = await makePostApiCall(`updateOutputItemCostPercentage`, {
        wip_id: wipId,
        production_cost_percentage: costPercentage,
        items: outgoingSelectedItems,
      });

      // Close progress modal
      setEmptyModelOpen({ open: false, childComponent: null });

      if (callResult.success) {
        setShowSimpleNotification({
          show: true,
          title: "Price group created",
          type: BannerNotificationType.Success,
        });
        setWip({
          ...wip,
          outgoing_items: callResult.data.items,
          outgoing_info: callResult.data.info,
        });

        calculateCostForWipOutgoingItems();

        setOutgoingRowDetails({});
        setOutgoingSubRowDetails({});
        setOutgoingSelectedItems([]);
        if (outgoingParentRow) {
          fetchOutgoingRowDetails(outgoingParentRow, false);
        }
      } else {
        if (callResult.errorStatus === 401) {
          logout();
        }
        showErrorModal(callResult.data.message);
      }
      return callResult;
    }
  };

  const openAddPriceGroupModal = () => {
    setEmptyModelOpen({
      open: true,
      childComponent: (
        <AddPriceGroupModal
          items={outgoingSelectedItems}
          createPriceGroup={onCreatePriceGroup}
          setEmptyModelOpen={setEmptyModelOpen}
        />
      ),
    });
  };

  const calculateCostForWipOutgoingItems = async function () {
    // Show progress modal
    setEmptyModelOpen({
      open: true,
      childComponent: <ProgressModal message="Calculating costs..." />,
    });

    let callResult = await makePostApiCall(`calculateCostForWipOutgoingItems`, {
      wip_id: wipId,
    });

    // Close progress modal
    setEmptyModelOpen({ open: false, childComponent: null });

    if (callResult.success) {
      setShowSimpleNotification({
        show: true,
        title: "Costs calculated",
        type: BannerNotificationType.Success,
      });
      setWip({
        ...wip,
        outgoing_items: callResult.data.items,
        outgoing_info: callResult.data.info,
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const printWipDetailsReport = async function () {
    let callResult = await makePostApiCall(`generateWipDetailsReport/${wipId}`);

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const printWipSummaryReport = async function () {
    let callResult = await makePostApiCall(`generateWipSummaryReport/${wipId}`);

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const printWipDerivativesReport = async function () {
    let callResult = await makePostApiCall(
      `generateWipDerivativesReport/${wipId}`,
    );

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const fetchRowDetails = async (rowData) => {
    setLoadingRows((prev) => ({ ...prev, [rowData.unique_id]: true }));

    let callResult = await makeGetApiCall(
      `wipItems/incoming/${wipId}/${rowData.plu}/${rowData.lot_number}/${rowData.unique_id}/${rowData.date}`,
    );
    if (callResult.success) {
      setRowDetails({ ...rowDetails, [rowData.unique_id]: callResult.data });
      setLoadingRows({ ...loadingRows, [rowData.unique_id]: false });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const fetchSubRowDetails = async (rowData) => {
    setLastRow(rowData);
    setSubLoadingRows((prev) => ({ ...prev, [rowData.unique_id]: true }));

    let callResult = await makeGetApiCall(
      `wipItems/incoming/pallets/${rowData.pallet_id}`,
    );
    if (callResult.success) {
      setSubRowDetails({
        ...subRowDetails,
        [rowData.unique_id]: callResult.data,
      });
      setSubLoadingRows({ ...subLoadingRows, [rowData.unique_id]: false });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const fetchOutgoingRowDetails = async (rowData, showLoading = true) => {
    setOutgoingParentRow(rowData);
    if (showLoading) {
      setOutgoingLoadingRows((prev) => ({
        ...prev,
        [rowData.unique_id]: true,
      }));
    }

    let callResult = await makeGetApiCall(
      `wipItems/outgoing/${wipId}/${rowData.plu}/${rowData.lot_number}`,
    );
    if (callResult.success) {
      setOutgoingRowDetails({
        ...outgoingRowDetails,
        [rowData.unique_id]: callResult.data,
      });
      setOutgoingLoadingRows({
        ...outgoingLoadingRows,
        [rowData.unique_id]: false,
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const fetchOutgoingSubRowDetails = async (rowData) => {
    setOutgoingLastRow(rowData);
    setOutgoingSubLoadingRows((prev) => ({
      ...prev,
      [rowData.unique_id]: true,
    }));

    let callResult = await makeGetApiCall(
      `wipItems/outgoing/pallets/${rowData.pallet_id}`,
    );
    if (callResult.success) {
      setOutgoingSubRowDetails({
        ...outgoingSubRowDetails,
        [rowData.unique_id]: callResult.data,
      });
      setOutgoingSubLoadingRows({
        ...outgoingSubLoadingRows,
        [rowData.unique_id]: false,
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const onWipItemTotalCostEditComplete = async function (payload) {
    let callResult = await makePostApiCall(
      `updateLegacyItemTotalCostFromWip`,
      payload,
    );
    if (callResult.success) {
      getWipDetails();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  return {
    emptyModal,
    setEmptyModelOpen,
    showSimpleNotification,
    setShowSimpleNotification,
    roomOptions,
    getRoomOptionsForDropdown,
    wip,
    loading,
    onAddItemToWipByBarcode,
    onRemoveItemWipByBarcode,
    removeReasonFromWipOptions,
    getRemoveReasonsOptionsForDropdown,
    onGetWipItemByBarcode,
    updateWip,
    getWipDetails,
    openSlideForManufacturedItemDetails,
    slideOver,
    setSlideOver,
    onAskReturnIncomingItem,
    onAskDeleteWipOutputItem,
    openAddPriceGroupModal,
    setOutgoingSelectedItems,
    outgoingSelectedItems,
    calculateCostForWipOutgoingItems,
    printWipDetailsReport,
    printWipSummaryReport,
    printWipDerivativesReport,
    showErrorModal,
    loadingRows,
    rowDetails,
    fetchSubRowDetails,
    subRowDetails,
    subLoadingRows,
    fetchRowDetails,
    fetchOutgoingRowDetails,
    outgoingLoadingRows,
    outgoingRowDetails,
    fetchOutgoingSubRowDetails,
    outgoingSubRowDetails,
    outgoingSubLoadingRows,
    setOutgoingRowDetails,
    setOutgoingSubRowDetails,
    onWipItemTotalCostEditComplete,
    setLoading,
  };
}
